import { useRouteCountry } from "@/composables/route/useCountry";
import { defineStore } from "pinia";

import {
  emptyAccount,
  useAccountStore,
} from "@/composables/account/useAccountStore";
import { useRouteCompany } from "@/composables/route/useCompany";
import { useRouteGenerator } from "@/composables/route/useGenerator";
import { LANGUAGE_TO_COUNTRY } from "@/constants";
import {
  Account,
  AccountUpdateResult,
  Company,
  CompanySaveResult,
  Generator,
} from "@/types";
import { cloneDeep, find, findIndex, includes } from "lodash-es";
import { computed, ref, watch } from "vue";

export function getCountryFromCompanyOrLanguage(
  accountData: Account,
  companyId: Company["id"]
) {
  const selectedCompany = find(accountData.companies, { id: companyId });

  if (!selectedCompany) {
    return LANGUAGE_TO_COUNTRY[
      accountData.language as keyof typeof LANGUAGE_TO_COUNTRY
    ];
  }

  return selectedCompany.country;
}

/**
 * Narrows the answer to a change that saved a company. The server names the company it saved — the
 * one the request named, or the one it created for the request — so a missing id is a broken answer.
 *
 * @throws {Error} When the answer names no company.
 */
export function expectSavedCompany(
  result: AccountUpdateResult
): CompanySaveResult {
  const { company_id } = result;
  if (typeof company_id !== "number") {
    throw new Error("The server answered a company save without its id.");
  }
  return { ...result, company_id };
}

const defaultCompany: Partial<Company> = {
  id: undefined,
  name: "",
  address: "",
  registry_code: "",
  has_registered: false,
  generators: [],
  documents: [],
  registry_devices: [],

  vat: null,
  bank_account: null,
  bank_name: null,
  bank_swift: null,
  payment_header: "",
  fee_model: "",
  registry_excluded_devices: [],
  is_test_company: false,
  country: undefined,
};

const defaultGenerator: Generator = {
  id: undefined,
  company_id: undefined,
  grid_contract: "",
  device_country: null,
  solar_panel_model: null,
  has_subsidy: null,
  has_registered: false,
  device_eic: "",
  device_address: "",
  device_power: null,
  device_name: "",
  device_technology: "",
  grid_contract_start_date: null,
  license_number: "",
  files: null,
  subsidies: [],
  battery: null,
  ppa: null,
  application_information: {},
};

export const useSignupStore = defineStore("signup", () => {
  // The URL's ?country wins, so a picker change, a link, and back/forward all agree; without one the
  // page entering signup supplies the account's own country (see `initialize`).
  const routeCountry = useRouteCountry();
  const fallbackCountry = ref("");
  const country = computed(
    () => routeCountry.value?.toUpperCase() || fallbackCountry.value
  );
  const companyId = useRouteCompany();
  const generatorId = useRouteGenerator();

  const account = ref<Account>(emptyAccount());

  const emptyCompanyIndex = computed(() =>
    findIndex(account.value.companies, ({ id }) =>
      includes([undefined, null], id)
    )
  );
  const noEmptyCompany = computed(() => emptyCompanyIndex.value === -1);

  const companyIndex = computed(() => {
    if (!account.value) return -1;
    if (!companyId.value) return emptyCompanyIndex.value;
    return findIndex(account.value.companies, { id: companyId.value });
  });

  const company = computed(() => {
    if (!account.value?.companies?.[companyIndex.value]) return null;
    return account.value.companies[companyIndex.value];
  });

  /** Adds the unsaved company a new owner's steps edit — unless the URL names a saved one to edit instead. */
  const createCompanyIfMissing = async () => {
    if (companyId.value) return;
    if (noEmptyCompany.value) {
      const newCompany = {
        ...defaultCompany,
        country: country.value,
      } as Company;

      account.value?.companies.push(newCompany);
    }
  };

  const createGeneratorIfMissing = async () => {
    if (companyIndex.value === -1 || !account.value.companies) {
      throw new Error("No company found to add generator to");
    }
    if (emptyGeneratorIndex.value === -1) {
      const newGenerator: Generator = {
        ...defaultGenerator,
        company_id: account.value.companies[companyIndex.value].id,
        device_country: account.value.companies[companyIndex.value].country,
      };

      account.value.companies[companyIndex.value].generators.push(newGenerator);
    }
  };

  const removeEmptyCompanyIfExists = () => {
    if (noEmptyCompany.value === true) return;
    account.value?.companies.splice(emptyCompanyIndex.value, 1);
  };

  const emptyGeneratorIndex = computed(() => {
    if (!account.value?.companies) return -1;
    if (companyIndex.value === -1) return -1;
    return findIndex(
      account.value.companies[companyIndex.value].generators,
      ({ id }) => includes([undefined, null], id)
    );
  });

  const generatorIndex = computed(() => {
    if (companyIndex.value === -1 || !generatorId.value)
      return emptyGeneratorIndex.value;

    return findIndex(account.value.companies[companyIndex.value].generators, {
      id: generatorId.value,
    });
  });

  function initialize({
    defaultCountry,
    initialAccount,
  }: {
    defaultCountry?: string;
    initialAccount: Account;
  }) {
    fallbackCountry.value = defaultCountry?.toUpperCase() ?? "";
    if (initialAccount) adoptAnswer(initialAccount);
  }

  // The unsaved company was started for the previous country; the flow for the new one starts its own.
  watch(country, () => removeEmptyCompanyIfExists(), { flush: "sync" });

  const updateAccount = (updatedAccountData: Partial<Account>) => {
    Object.assign(account.value, updatedAccountData);
  };
  /**
   * Makes a tree the account store also holds the draft. The flows edit the draft in place, so it
   * takes a deep copy and shares no objects with that store (`cloneDeep`: `structuredClone`
   * refuses Vue proxies).
   */
  function adoptAnswer(tree: Readonly<Account>): void {
    Object.assign(account.value, cloneDeep(tree));
  }
  const updateCompany = (updatedCompany: Partial<Company>) => {
    Object.assign(account.value.companies[companyIndex.value], updatedCompany);
  };
  const updateGenerator = (updatedGenerator: Generator) => {
    Object.assign(
      account.value.companies?.[companyIndex.value]?.generators?.[
        generatorIndex.value
      ],
      updatedGenerator
    );
  };

  /**
   * Saves the draft's company — the one the URL names, or the unsaved one while it names none —
   * together with the draft's account fields, and adopts the server's answer as the draft. The
   * account store takes the answer too, so every other reader sees the save.
   *
   * @throws {Error} When the draft has no such company; nothing is posted then. Also when the answer
   * names no company, after both stores took it.
   */
  async function saveDraft(url: string): Promise<CompanySaveResult> {
    if (!company.value) {
      throw new Error("The signup draft has no company to save.");
    }
    const result = await useAccountStore().postAccountData(
      url,
      { ...account.value, companies: [company.value] },
      company.value.id
    );
    adoptAnswer(result.accountData);
    return expectSavedCompany(result);
  }

  /**
   * Takes an account tree a signup step received without going through `saveDraft` — the file
   * uploads post their own requests — into both the account store and the draft. It skips
   * `postAccountChange`'s re-read after an overlapping read safely: the answer arrives by an emit,
   * which Vue drops once the flow has unmounted, so a page left mid-upload never delivers it.
   */
  function takeAnswer(accountData: Account): void {
    useAccountStore().applyAccountUpdate(accountData);
    adoptAnswer(accountData);
  }

  return {
    // State
    account,
    country,
    // Computed
    companyIndex,
    company,
    generatorIndex,
    // Actions
    initialize,
    createCompanyIfMissing,
    removeEmptyCompanyIfExists,
    createGeneratorIfMissing,
    updateAccount,
    updateCompany,
    updateGenerator,
    saveDraft,
    takeAnswer,
  };
});
